import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { Button, Skeleton } from '@liftango/ui-elements';
import { Icon } from '@liftango/ui-icons';
import { opsColors, opsFontFamily, opsFontSize, opsFontWeight, opsRadius, opsSpace } from '../../../../../theme/opsTokens';
import type { RideTiming, ShiftSummary } from '../../../../../services/simulation/runReport';
import type { RunReport } from '../useRunReport';
import { formatClock, formatNumber } from './reportFormat';
import { kmLabel, percentLabel, rowActivation, seatLabel, vehicleName } from './railLabels';

const ICON_SIZE = 14;
const KM_SKELETON = { width: '40px', height: '16px' } as const;
const MONO = { fontFamily: opsFontFamily.mono, fontSize: opsFontSize.sm } as const;

export interface VehicleDetailProps {
  readonly report: RunReport;
  readonly shift: ShiftSummary;
  readonly onBack: () => void;
  readonly onJumpTo: (timeMs: number) => void;
}

export function VehicleDetail({ report, shift, onBack, onJumpTo }: VehicleDetailProps) {
  const { t, i18n } = useTranslation('simulation');
  const locale = i18n.language;
  const ridesById = useMemo(
    () => new Map<string, RideTiming>((report.derived?.rides ?? []).map((ride) => [ride.rideId, ride])),
    [report.derived]
  );
  const km = kmLabel(report, shift.shiftId, t, locale);
  const stats: ReadonlyArray<{ key: string; label: string; value: React.ReactNode }> = [
    { key: 'trips', label: t('results.report.rail.columns.trips'), value: formatNumber(shift.trips, locale) },
    { key: 'pax', label: t('results.report.rail.columns.pax'), value: formatNumber(shift.pax, locale) },
    { key: 'km', label: t('results.report.rail.columns.km'), value: km.kind === 'loading' ? <Skeleton {...KM_SKELETON} /> : km.text },
    { key: 'peakLoad', label: t('results.report.rail.columns.peakLoad'), value: percentLabel(shift.peakLoadFactor.standard, t, locale) },
  ];

  return (
    <Box data-testid="sim-results.report.rail.vehicle-detail" sx={{ p: opsSpace[4], display: 'grid', gap: opsSpace[4] }}>
      <Box>
        <Button kind="ghost" size="sm" icon={<Icon name="arrow-left" size={ICON_SIZE} />} onClick={onBack}>
          {t('results.report.rail.allVehicles')}
        </Button>
      </Box>
      <Box>
        <Box component="h3" sx={{ m: 0, fontSize: opsFontSize.h2, fontWeight: opsFontWeight.semibold, color: opsColors.neutral.ink }}>
          {vehicleName(shift, t)}
        </Box>
        <Box sx={{ color: opsColors.neutral.inkSoft, fontSize: opsFontSize.sm }}>
          {t('results.report.rail.shiftLine', {
            start: formatClock(shift.startMs, locale),
            end: formatClock(shift.endMs, locale),
            seats: seatLabel(shift, t),
          })}
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))`, gap: opsSpace[2] }}>
        {stats.map((stat) => (
          <Box
            key={stat.key}
            data-testid={`sim-results.report.rail.detail.${stat.key}`}
            sx={{ border: `1px solid ${opsColors.neutral.line}`, borderRadius: opsRadius.md, p: opsSpace[2] }}
          >
            <Box sx={{ color: opsColors.neutral.inkMute, fontSize: opsFontSize.eyebrow, fontWeight: opsFontWeight.bold, textTransform: 'uppercase' }}>
              {stat.label}
            </Box>
            <Box sx={{ ...MONO, fontWeight: opsFontWeight.bold, fontSize: opsFontSize.lg }}>{stat.value}</Box>
          </Box>
        ))}
      </Box>
      <Table size="small" aria-label={t('results.report.rail.stopsTable')}>
        <TableBody>
          {shift.stops.map((stop) => {
            const ride = ridesById.get(stop.rideId);
            return (
              <TableRow
                key={`${stop.kind}-${stop.rideId}`}
                hover
                data-testid="sim-results.report.rail.stop"
                aria-label={t(stop.kind === 'pick' ? 'results.report.rail.jumpToPickUp' : 'results.report.rail.jumpToDropOff', { time: formatClock(stop.atMs, locale) })}
                {...rowActivation(() => onJumpTo(stop.atMs))}
              >
                <TableCell sx={MONO}>{formatClock(stop.atMs, locale)}</TableCell>
                <TableCell>{t(stop.kind === 'pick' ? 'results.report.rail.pickUp' : 'results.report.rail.dropOff')}</TableCell>
                <TableCell align="right" sx={MONO}>
                  {ride ? formatNumber(ride.pax.standard + ride.pax.wheelchair, locale) : t('results.report.noValue')}
                </TableCell>
                <TableCell sx={{ ...MONO, color: opsColors.neutral.inkMute }}>{ride?.externalReferenceId ?? t('results.report.noValue')}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
